import React, { PropsWithChildren, ReactElement, useCallback } from 'react';
import { Col, FlexboxGrid, Grid, Row, Text } from 'rsuite';
import FlowProgressIndicator from '../user-feedback/FlowProgressIndicator';
import { useSelector } from 'react-redux';
import {
  selectCurrentStep,
  selectFlowSteps,
  selectNextStepPath,
  selectOriginPath,
  selectPrevStepPath,
} from '../../store/slices/flow/flowSelectors';
import {
  resetStepState,
  setCurrentStep,
  setStepCompletedStatus,
} from '../../store/slices/flow/flowSlice';
import usePlaceNavigate from '../../hooks/usePlaceNavigate';
import PageButtons, { PageButtonsProps } from './PageButtons';
import PortalHeader from './PortalHeader';
import { FLOWS_ENUM } from 'src/common/enums';
import { useAppDispatch } from 'src/store/store';

interface FlowPageLayoutProps extends PageButtonsProps {
  flow?: FLOWS_ENUM;
  omitNavigationForward?: boolean;
  subtitle?: string;
  title?: string;
}

export const FlowPageLayout = ({
  back,
  children,
  flow,
  next,
  omitNavigationForward,
  onBack,
  onNext,
  showBack,
  showNext,
  subtitle = '',
  title,
}: PropsWithChildren<FlowPageLayoutProps>): ReactElement => {
  const dispatch = useAppDispatch();
  const placeNavigate = usePlaceNavigate();
  const selectedSteps = useSelector(selectFlowSteps);
  const selectedCurrentStep = useSelector(selectCurrentStep);
  const selectedNextStepPath = useSelector(selectNextStepPath);
  const selectedPrevStepPath = useSelector(selectPrevStepPath);
  const selectedOriginPath = useSelector(selectOriginPath);

  const goToPrevStep = () => {
    if (onBack) {
      onBack();
    }
    // The following should be moved to the flowSlices
    let prevPath = '/' + selectedPrevStepPath;
    if (selectedCurrentStep <= 1) {
      prevPath = selectedOriginPath;
      dispatch(resetStepState());
    } else {
      dispatch(
        setStepCompletedStatus({ stepNum: selectedCurrentStep, status: false })
      );
      dispatch(setCurrentStep(selectedCurrentStep - 1));
    }
    placeNavigate(prevPath);
  };

  const goToNextStep = useCallback(async () => {
    if (onNext) {
      onNext();
    }
    // The following should be moved to the flowSlices
    if (!omitNavigationForward) {
      dispatch(
        setStepCompletedStatus({ stepNum: selectedCurrentStep, status: true })
      );
      dispatch(setCurrentStep(selectedCurrentStep + 1));
      placeNavigate('/' + selectedNextStepPath);
    }
  }, [omitNavigationForward, onNext, selectedCurrentStep]);

  return (
    <FlexboxGrid justify="space-between">
      <FlexboxGrid.Item colspan={24}>
        <PortalHeader flow={flow} />
      </FlexboxGrid.Item>
      <FlexboxGrid.Item colspan={24}>
        <Grid
          className="mt-24 sm-mt-0 pv-24 sm-pb-0"
          fluid
        >
          <Row role="presentation">
            <Col
              role="presentation"
              className="flow-page-horizontal-progress-indicator"
              xs={24}
              lgHidden
              xlHidden
              xxlHidden
            >
              {selectedSteps != null && (
                <FlowProgressIndicator
                  indicatorState={selectedSteps}
                  direction="horizontal"
                  currentStateIndex={selectedCurrentStep}
                />
              )}
            </Col>
          </Row>
          <Row>
            <Col
              className="flow-page-layout-container"
              xs={24}
              lg={16}
            >
              <div className="flow-page-layout-panel-left">
                <Text
                  className="header-text-h3 mb-24"
                  style={{ marginLeft: '-3px' }}
                >
                  {title}
                </Text>
                {subtitle?.length > 0 && (
                  <Text className="mb-24">{subtitle}</Text>
                )}
                <div className="content-section">{children}</div>
              </div>
              <div className=" flow-page-layout-panel-left">
                <PageButtons
                  onNext={goToNextStep}
                  onBack={goToPrevStep}
                  showBack={showBack}
                  showNext={showNext}
                  next={next}
                  back={back}
                />
              </div>
            </Col>
            <Col
              lg={8}
              xsHidden
              smHidden
              mdHidden
              className="flow-page-layout-panel-right"
            >
              {!!selectedSteps && (
                <FlowProgressIndicator
                  indicatorState={selectedSteps}
                  currentStateIndex={selectedCurrentStep - 1}
                />
              )}
            </Col>
          </Row>
        </Grid>
      </FlexboxGrid.Item>
    </FlexboxGrid>
  );
};

export default FlowPageLayout;
